import * as React from 'react';
import { cn } from '@/lib/utils';
import type { BookingStatus } from '@/lib/status-colors';
import type { BadgeTone } from '@/components/ui/badge';

/*
 * StatusFilterChip — interactive filter button that visually matches
 * <StatusBadge> tones. Used on bookings list pages to filter by status.
 *
 * Inactive: soft tone bg (same as StatusBadge), no border, muted.
 * Active:   same soft bg + ring for selection affordance.
 * `all`:    neutral chip; active variant inverts to ink/white.
 */

const STATUS_TO_TONE: Record<BookingStatus, BadgeTone> = {
  pending:   'warn',
  confirmed: 'success',
  cancelled: 'danger',
  completed: 'info',
  no_show:   'neutral',
};

const TONE_BG: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-soft',
  info:    'bg-brand-50 text-brand-700',
  success: 'bg-success-soft text-success-text',
  warn:    'bg-warn-soft text-warn-text',
  danger:  'bg-danger-soft text-danger-text',
};

const TONE_RING: Record<BadgeTone, string> = {
  neutral: 'ring-line-strong',
  info:    'ring-brand-500',
  success: 'ring-success',
  warn:    'ring-warn',
  danger:  'ring-danger',
};

export interface StatusFilterChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  status: BookingStatus | 'all';
  active: boolean;
  label: string;
  count?: number;
}

export const StatusFilterChip = React.forwardRef<HTMLButtonElement, StatusFilterChipProps>(
  ({ status, active, label, count, className, ...props }, ref) => {
    const isAll = status === 'all';
    const tone = isAll ? null : STATUS_TO_TONE[status];

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-xs font-semibold whitespace-nowrap transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
          isAll
            ? active
              ? 'bg-ink text-white'
              : 'bg-surface text-ink border border-line hover:bg-surface-2'
            : cn(
                TONE_BG[tone!],
                active ? cn('ring-2 ring-offset-1', TONE_RING[tone!]) : 'opacity-75 hover:opacity-100',
              ),
          className,
        )}
        {...props}
      >
        <span>{label}</span>
        {count !== undefined && (
          <span className={cn('tabular-nums', active ? '' : 'opacity-80')}>· {count}</span>
        )}
      </button>
    );
  },
);
StatusFilterChip.displayName = 'StatusFilterChip';
